/**
 * Deck-creation canvas — the left-pane visualization on
 * `/admin/decks/new` once a `createDeckDraft` or `iterateOnDeckDraft`
 * tool-call lands. Shows:
 *
 *   - A six-chip phase strip (fork → clone → ai_gen → apply → commit → push)
 *   - A growing file tree as the model emits each file
 *   - The currently-writing file's content streaming in
 *   - An error overlay when a snapshot lands with `phase: "error"`
 *
 * Consumes `messages` from `useAgentChat` (or any compatible message
 * shape — see `extractLatestCall.ts` for the loose type). Self-
 * contained: the route just mounts this with messages and an optional
 * `onRetry` callback; everything else is internal.
 *
 * Issue #178 sub-pieces (1) + (3).
 */

import {
  extractLatestDeckCreationCall,
  isDeckCreationSnapshot,
  isDeckDraftToolResult,
  type DeckCreationMessage,
} from "./extractLatestCall";
import { PhaseStrip } from "./PhaseStrip";
import { FileTree } from "./FileTree";
import { FileContent } from "./FileContent";
import { ErrorOverlay } from "./ErrorOverlay";
import { ComposingOverlay } from "./ComposingOverlay";
import type { DeckCreationSnapshot, SlidePlan } from "@/lib/deck-creation-snapshot";

export interface DeckCreationCanvasProps {
  messages: ReadonlyArray<DeckCreationMessage>;
  /**
   * Deck slug — used to strip the `src/decks/public/<slug>/` prefix
   * from displayed file paths. Optional: when unset the canvas
   * infers the slug from the first emitted file's path. Useful on
   * `/admin/decks/new` where the model picks the slug from the
   * user's prompt and the route doesn't know it ahead of time.
   */
  slug?: string;
  /**
   * Called when the user clicks "Retry" on the error overlay. Hidden
   * when undefined. Typically wired to `sendMessage(<lastUserPrompt>)`
   * from the route's `useAgentChat`.
   */
  onRetry?: () => void;
}

/**
 * Infer the deck slug from the first emitted file's path. Paths look
 * like `src/decks/public/<slug>/meta.ts`; we grab the segment after
 * `src/decks/public/`. Returns `undefined` if no file has been emitted
 * yet or the path doesn't match the expected shape.
 */
function inferSlug(files: Array<{ path: string }>): string | undefined {
  if (files.length === 0) return undefined;
  const path = files[0]?.path;
  if (!path) return undefined;
  const match = path.match(/^src\/decks\/public\/([^/]+)\//);
  return match?.[1];
}

function isGeneratedSlideFile(
  files: DeckCreationSnapshot["files"],
  slide: SlidePlan,
): boolean {
  return files.some(
    (file) => file.state === "done" && file.path.endsWith(`-${slide.id}.tsx`),
  );
}

function PlannedSlideList({
  plannedSlides,
  files,
  activeSlideId,
  statusMessage,
}: {
  plannedSlides: SlidePlan[];
  files: DeckCreationSnapshot["files"];
  activeSlideId?: string;
  statusMessage?: string;
}) {
  if (plannedSlides.length === 0) return null;
  const generatedCount = plannedSlides.filter((slide) =>
    isGeneratedSlideFile(files, slide),
  ).length;
  return (
    <section
      data-testid="deck-creation-plan-list"
      className="rounded-lg border border-cf-text/10 bg-cf-bg-100 p-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-cf-text-subtle">
            Planned slides
          </p>
          <p className="mt-1 text-xs text-cf-text-muted">
            {generatedCount} of {plannedSlides.length} slide files generated
          </p>
          {statusMessage ? (
            <p className="mt-1 text-xs text-cf-orange">{statusMessage}</p>
          ) : null}
        </div>
      </div>
      <ol className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {plannedSlides.map((slide, index) => {
          const done = isGeneratedSlideFile(files, slide);
          const state = done
            ? "done"
            : activeSlideId === slide.id
              ? "current"
              : "pending";
          return (
            <li
              key={slide.id}
              data-testid={`deck-creation-plan-slide-${slide.id}`}
              data-state={state}
              className={`rounded-md border px-3 py-2 ${
                state === "current"
                  ? "border-cf-orange bg-cf-orange/5 text-cf-orange"
                  : state === "done"
                    ? "border-cf-text/15 bg-cf-text/5 text-cf-text"
                    : "border-cf-text/10 text-cf-text-muted"
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="mt-0.5 font-mono text-[10px] tabular-nums text-cf-text-subtle">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{slide.title}</p>
                  <p className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-[0.14em] text-cf-text-subtle">
                    {state}
                  </p>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

const PHASE_PROGRESS_COPY: Partial<
  Record<DeckCreationSnapshot["phase"], { title: string; body: string }>
> = {
  fork: {
    title: "Preparing the draft",
    body: "Creating or opening the Artifacts repository for this deck.",
  },
  clone: {
    title: "Starting the Sandbox",
    body: "A Cloudflare Sandbox container is warming up and cloning the draft repository. Cold starts can take a couple of minutes.",
  },
  ai_gen: {
    title: "Planning your deck",
    body: "The model is planning the deck structure before slide files appear.",
  },
  apply: {
    title: "Applying generated files",
    body: "Writing the generated source files into the Sandbox checkout.",
  },
  commit: {
    title: "Committing the draft",
    body: "Creating a git commit in your Artifacts draft repository.",
  },
  push: {
    title: "Pushing the draft",
    body: "Pushing the commit back to your Artifacts draft repository.",
  },
};

function PhaseProgressPanel({ snapshot }: { snapshot: DeckCreationSnapshot }) {
  const copy = PHASE_PROGRESS_COPY[snapshot.phase] ?? {
    title: "Working",
    body: "The agent is working on the current step.",
  };
  return (
    <div
      data-testid="deck-creation-phase-progress"
      className="flex flex-1 flex-col items-center justify-center gap-5 rounded-lg border border-cf-text/10 bg-cf-bg-100 p-10 text-center"
    >
      <div aria-hidden="true" className="flex items-center gap-2">
        <span className="composing-dot h-2 w-2 rounded-full bg-cf-orange/70" />
        <span className="composing-dot composing-dot--delay-1 h-2 w-2 rounded-full bg-cf-orange/70" />
        <span className="composing-dot composing-dot--delay-2 h-2 w-2 rounded-full bg-cf-orange/70" />
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-cf-text-subtle">
          {snapshot.phase}
        </p>
        <h2 className="mt-2 text-2xl font-medium tracking-[-0.025em] text-cf-text">
          {copy.title}
        </h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-cf-text-muted">
          {snapshot.statusMessage ?? copy.body}
        </p>
      </div>
      {snapshot.draftId ? (
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-cf-text-subtle">
          {snapshot.draftId}
        </p>
      ) : null}
    </div>
  );
}

export function DeckCreationCanvas({
  messages,
  slug: slugProp,
  onRetry,
}: DeckCreationCanvasProps) {
  const call = extractLatestDeckCreationCall(messages);

  // Pre-call / pre-output state: tool hasn't emitted any yields yet
  // (still input-streaming or input-available). The route only mounts
  // this component once a tool-call lands, so this branch shows a
  // brief "warming up" state.
  if (!call || call.output === undefined) {
    return (
      <div
        data-testid="deck-creation-canvas"
        data-state="warming-up"
        className="flex h-full flex-col items-center justify-center gap-2 text-sm text-cf-text-muted"
      >
        <span className="font-mono text-xs uppercase tracking-wider animate-pulse">
          Starting…
        </span>
      </div>
    );
  }

  // Lean final tool result — model-facing, doesn't carry verbose
  // file content. Render the most-recently-known snapshot fields we
  // CAN derive (commit SHA, success/failure). The error state here
  // is for the rare case where the orchestrator yielded a final
  // DeckDraftToolResult without an intermediate "done" snapshot —
  // shouldn't happen in practice, but the component is robust to it.
  if (isDeckDraftToolResult(call.output)) {
    if (call.output.ok) {
      return (
        <div
          data-testid="deck-creation-canvas"
          data-state="done"
          className="flex h-full flex-col items-center justify-center gap-3 text-sm text-cf-text"
        >
          <span className="text-3xl">✓</span>
          <p className="text-cf-text">
            Deck created. Commit{" "}
            <code className="font-mono text-xs">
              {call.output.commitSha.slice(0, 7)}
            </code>
            .
          </p>
        </div>
      );
    }
    return (
      <div
        data-testid="deck-creation-canvas"
        data-state="error"
        className="flex h-full flex-col gap-4 p-6"
      >
        <ErrorOverlay
          message={call.output.error}
          {...(onRetry ? { onRetry } : {})}
        />
      </div>
    );
  }

  // The common case: an in-flight or terminal DeckCreationSnapshot.
  const snapshot = call.output;
  if (!isDeckCreationSnapshot(snapshot)) {
    // Unreachable — kept for exhaustive narrowing.
    return null;
  }

  const activeFile = snapshot.files.find((f) => f.state === "writing");
  const lastFile = snapshot.files[snapshot.files.length - 1];
  const fileToShow = activeFile ?? lastFile;
  const isErrored = snapshot.phase === "error";
  const slug = slugProp ?? inferSlug(snapshot.files);

  // During the silent `ai_gen` window — `generateObject` runs single-shot
  // and emits no files for ~1-3 min — replace the empty file-tree grid
  // with the composing overlay. PhaseStrip stays above so the user still
  // sees `ai_gen` as the active phase. See ComposingOverlay.tsx.
  const hasPlannedSlides =
    snapshot.plannedSlides !== undefined && snapshot.plannedSlides.length > 0;
  const isComposing =
    snapshot.phase === "ai_gen" && snapshot.files.length === 0 && !hasPlannedSlides;
  const showPhaseProgress = snapshot.files.length === 0 && !hasPlannedSlides;

  return (
    <div
      data-testid="deck-creation-canvas"
      data-state={snapshot.phase}
      className="flex h-full flex-col gap-4 p-6"
    >
      <PhaseStrip
        currentPhase={snapshot.phase}
        {...(snapshot.failedPhase ? { failedPhase: snapshot.failedPhase } : {})}
      />
      {isErrored ? (
        <ErrorOverlay
          message={snapshot.error ?? "An error occurred."}
          {...(snapshot.failedPhase ? { failedPhase: snapshot.failedPhase } : {})}
          {...(onRetry ? { onRetry } : {})}
        />
      ) : null}
      {isComposing ? (
        <ComposingOverlay />
      ) : showPhaseProgress ? (
        <PhaseProgressPanel snapshot={snapshot} />
      ) : (
        <div className="flex flex-1 flex-col gap-3 overflow-hidden">
          {snapshot.plannedSlides && snapshot.plannedSlides.length > 0 ? (
            <PlannedSlideList
              plannedSlides={snapshot.plannedSlides}
              files={snapshot.files}
              {...(snapshot.activeSlideId
                ? { activeSlideId: snapshot.activeSlideId }
                : {})}
              {...(snapshot.statusMessage
                ? { statusMessage: snapshot.statusMessage }
                : {})}
            />
          ) : null}
          <div className="grid flex-1 grid-cols-[minmax(180px,_240px)_1fr] gap-4 overflow-hidden">
            <aside className="overflow-y-auto rounded-lg border border-cf-text/10 bg-cf-bg-100 p-2">
              <FileTree
                files={snapshot.files}
                slug={slug}
                {...(activeFile?.path ? { activePath: activeFile.path } : {})}
              />
            </aside>
            <section className="overflow-hidden rounded-lg border border-cf-text/10 bg-cf-bg-100">
              <FileContent file={fileToShow} slug={slug} />
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
